import { type Static, Type } from "@sinclair/typebox";
import { Value } from "@sinclair/typebox/value";

const BusinessAuditTimestampSchema = Type.String({ minLength: 24, maxLength: 32 });
const BusinessAuditMoneyFenSchema = Type.Integer({
  minimum: 0,
  maximum: Number.MAX_SAFE_INTEGER,
});
const BusinessAuditSignedMoneyFenSchema = Type.Integer({
  minimum: Number.MIN_SAFE_INTEGER,
  maximum: Number.MAX_SAFE_INTEGER,
});

export const BusinessAuditRecordDetailSchema = Type.Union([
  Type.Object({ kind: Type.Literal("SUMMARY_ONLY") }, { additionalProperties: false }),
  Type.Object({ kind: Type.Literal("SOURCE_UNTRUSTED") }, { additionalProperties: false }),
  Type.Object(
    {
      kind: Type.Literal("PURCHASE_RECEIPT"),
      purchaseOrderNumber: Type.String({ pattern: "^CG-[0-9]{8}-[0-9A-F]{12}$" }),
      receiptNumber: Type.String({ pattern: "^SH-[0-9]{8}-[0-9A-F]{12}$" }),
      businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
      productLineCount: Type.Integer({ minimum: 1, maximum: 200 }),
      inventoryValueIncreaseFen: BusinessAuditMoneyFenSchema,
      payableIncreaseFen: BusinessAuditMoneyFenSchema,
      payableTotalFenBefore: BusinessAuditMoneyFenSchema,
      payableTotalFenAfter: BusinessAuditMoneyFenSchema,
      purchaseOrderStatusAfter: Type.Union([
        Type.Literal("PARTIALLY_RECEIVED"),
        Type.Literal("RECEIVED"),
      ]),
      cloudProjectionStatus: Type.Union([
        Type.Literal("PENDING"),
        Type.Literal("SYNCED"),
        Type.Literal("NEEDS_REVIEW"),
      ]),
      receivedAt: BusinessAuditTimestampSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("SESSION_VOID"),
      orderNumber: Type.String({ pattern: "^DD-[0-9]{8}-[0-9A-F]{12}$" }),
      businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
      tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
      reason: Type.String({ minLength: 2, maxLength: 200 }),
      previousOrderStatus: Type.Literal("OPEN"),
      currentOrderStatus: Type.Literal("SETTLED"),
      previousAmountFen: BusinessAuditMoneyFenSchema,
      currentAmountFen: Type.Literal(0),
      paidAmountFen: Type.Literal(0),
      refundedAmountFen: Type.Literal(0),
      cloudProjectionStatus: Type.Union([
        Type.Literal("PENDING"),
        Type.Literal("SYNCED"),
        Type.Literal("NEEDS_REVIEW"),
      ]),
      voidedAt: BusinessAuditTimestampSchema,
    },
    { additionalProperties: false },
  ),
  Type.Object(
    {
      kind: Type.Literal("SESSION_START_ADJUSTMENT"),
      orderNumber: Type.String({ pattern: "^DD-[0-9]{8}-[0-9A-F]{12}$" }),
      businessDate: Type.String({ pattern: "^[0-9]{4}-[0-9]{2}-[0-9]{2}$" }),
      tableNumber: Type.String({ minLength: 1, maxLength: 32 }),
      reason: Type.String({ minLength: 2, maxLength: 200 }),
      previousStartedAt: BusinessAuditTimestampSchema,
      currentStartedAt: BusinessAuditTimestampSchema,
      previousTableFeeFen: BusinessAuditMoneyFenSchema,
      currentTableFeeFen: BusinessAuditMoneyFenSchema,
      adjustmentFen: BusinessAuditSignedMoneyFenSchema,
      cloudProjectionStatus: Type.Union([
        Type.Literal("PENDING"),
        Type.Literal("SYNCED"),
        Type.Literal("NEEDS_REVIEW"),
      ]),
      adjustedAt: BusinessAuditTimestampSchema,
    },
    { additionalProperties: false },
  ),
]);
export type BusinessAuditRecordDetail = Static<typeof BusinessAuditRecordDetailSchema>;

export const BusinessAuditTimelineRecordSchema = Type.Object(
  {
    recordNumber: Type.String({ pattern: "^AUD-[0-9]{8}-[0-9A-F]{12}$" }),
    actionLabel: Type.String({ minLength: 1, maxLength: 120 }),
    objectLabel: Type.String({ minLength: 1, maxLength: 160 }),
    actorDisplayName: Type.String({ minLength: 1, maxLength: 120 }),
    authorizerDisplayName: Type.Union([Type.String({ minLength: 1, maxLength: 120 }), Type.Null()]),
    occurredAt: BusinessAuditTimestampSchema,
    detail: BusinessAuditRecordDetailSchema,
  },
  { additionalProperties: false },
);
export type BusinessAuditTimelineRecord = Static<typeof BusinessAuditTimelineRecordSchema>;

export const BusinessAuditTimelineReadModelSchema = Type.Object(
  {
    tenantId: Type.String({ minLength: 1, maxLength: 128 }),
    storeId: Type.String({ minLength: 1, maxLength: 128 }),
    generatedAt: BusinessAuditTimestampSchema,
    totalRecords: Type.Integer({ minimum: 0, maximum: Number.MAX_SAFE_INTEGER }),
    records: Type.Array(BusinessAuditTimelineRecordSchema, { maxItems: 1_000 }),
    truncated: Type.Boolean(),
  },
  { additionalProperties: false },
);
export type BusinessAuditTimelineReadModel = Static<typeof BusinessAuditTimelineReadModelSchema>;

function isCanonicalTimestamp(value: string): boolean {
  const milliseconds = Date.parse(value);
  return Number.isFinite(milliseconds) && new Date(milliseconds).toISOString() === value;
}

export function isBusinessAuditTimelineReadModel(
  value: unknown,
): value is BusinessAuditTimelineReadModel {
  if (!Value.Check(BusinessAuditTimelineReadModelSchema, value)) return false;
  if (!isCanonicalTimestamp(value.generatedAt)) return false;
  if (value.totalRecords < value.records.length) return false;
  if (value.truncated !== value.totalRecords > value.records.length) return false;
  const recordNumbers = new Set<string>();
  let previousOccurredAt = Number.POSITIVE_INFINITY;
  for (const record of value.records) {
    const occurredAt = Date.parse(record.occurredAt);
    if (
      !isCanonicalTimestamp(record.occurredAt) ||
      occurredAt > previousOccurredAt ||
      recordNumbers.has(record.recordNumber)
    ) {
      return false;
    }
    previousOccurredAt = occurredAt;
    recordNumbers.add(record.recordNumber);
    if (record.detail.kind === "PURCHASE_RECEIPT") {
      const businessDateMilliseconds = Date.parse(`${record.detail.businessDate}T00:00:00.000Z`);
      if (
        !Number.isFinite(businessDateMilliseconds) ||
        new Date(businessDateMilliseconds).toISOString() !==
          `${record.detail.businessDate}T00:00:00.000Z` ||
        !isCanonicalTimestamp(record.detail.receivedAt) ||
        !Number.isSafeInteger(
          record.detail.payableTotalFenBefore + record.detail.payableIncreaseFen,
        ) ||
        record.detail.payableTotalFenBefore + record.detail.payableIncreaseFen !==
          record.detail.payableTotalFenAfter
      ) {
        return false;
      }
    }
    if (record.detail.kind === "SESSION_VOID") {
      const businessDateMilliseconds = Date.parse(`${record.detail.businessDate}T00:00:00.000Z`);
      if (
        !Number.isFinite(businessDateMilliseconds) ||
        new Date(businessDateMilliseconds).toISOString() !==
          `${record.detail.businessDate}T00:00:00.000Z` ||
        record.detail.orderNumber.slice(3, 11) !== record.detail.businessDate.replaceAll("-", "") ||
        !isCanonicalTimestamp(record.detail.voidedAt) ||
        record.detail.voidedAt !== record.occurredAt ||
        !Number.isSafeInteger(record.detail.previousAmountFen)
      ) {
        return false;
      }
    }
    if (record.detail.kind === "SESSION_START_ADJUSTMENT") {
      const businessDateMilliseconds = Date.parse(`${record.detail.businessDate}T00:00:00.000Z`);
      if (
        !Number.isFinite(businessDateMilliseconds) ||
        new Date(businessDateMilliseconds).toISOString() !==
          `${record.detail.businessDate}T00:00:00.000Z` ||
        record.detail.orderNumber.slice(3, 11) !== record.detail.businessDate.replaceAll("-", "") ||
        !isCanonicalTimestamp(record.detail.previousStartedAt) ||
        !isCanonicalTimestamp(record.detail.currentStartedAt) ||
        record.detail.previousStartedAt === record.detail.currentStartedAt ||
        !isCanonicalTimestamp(record.detail.adjustedAt) ||
        record.detail.adjustedAt !== record.occurredAt ||
        !Number.isSafeInteger(
          record.detail.currentTableFeeFen - record.detail.previousTableFeeFen,
        ) ||
        record.detail.adjustmentFen !==
          record.detail.currentTableFeeFen - record.detail.previousTableFeeFen
      ) {
        return false;
      }
    }
  }
  return true;
}

const BUSINESS_AUDIT_CSV_HEADERS = [
  "操作记录号",
  "操作时间",
  "操作人（当前姓名）",
  "操作内容",
  "关联业务",
  "现场授权人（当前姓名）",
  "详情状态",
  "采购单号",
  "收货单号",
  "营业日",
  "商品行数",
  "库存价值增加（分）",
  "应付增加（分）",
  "应付前（分）",
  "应付后（分）",
  "采购单状态",
  "云端同步状态",
  "收货完成时间",
  "台单号",
  "球台",
  "作废原因",
  "作废前台单状态",
  "作废后台单状态",
  "作废前金额（分）",
  "作废后金额（分）",
  "实收（分）",
  "退款（分）",
  "作废完成时间",
  "调时原因",
  "调整前开台时间",
  "调整后开台时间",
  "调整前台费（分）",
  "调整后台费（分）",
  "台费差额（分）",
  "调时完成时间",
] as const;

function csvDetailFields(record: BusinessAuditTimelineRecord): Array<string | number> {
  if (record.detail.kind === "SUMMARY_ONLY") {
    return ["仅摘要", ...Array.from({ length: 28 }, () => "")];
  }
  if (record.detail.kind === "SOURCE_UNTRUSTED") {
    return ["详情暂不可安全确认", ...Array.from({ length: 28 }, () => "")];
  }
  if (record.detail.kind === "PURCHASE_RECEIPT") {
    const purchaseOrderStatus =
      record.detail.purchaseOrderStatusAfter === "RECEIVED"
        ? "采购单已全部收货"
        : "本次后仍有商品待收";
    const cloudProjectionStatus =
      record.detail.cloudProjectionStatus === "SYNCED"
        ? "门店收货与云端记录已同步"
        : record.detail.cloudProjectionStatus === "PENDING"
          ? "门店已完成收货，正在同步到云端"
          : "门店收货已完成，对应云端记录待复核；其他经营可继续";
    return [
      "采购收货已完成",
      record.detail.purchaseOrderNumber,
      record.detail.receiptNumber,
      record.detail.businessDate,
      record.detail.productLineCount,
      record.detail.inventoryValueIncreaseFen,
      record.detail.payableIncreaseFen,
      record.detail.payableTotalFenBefore,
      record.detail.payableTotalFenAfter,
      purchaseOrderStatus,
      cloudProjectionStatus,
      record.detail.receivedAt,
      ...Array.from({ length: 17 }, () => ""),
    ];
  }
  if (record.detail.kind === "SESSION_VOID") {
    const cloudProjectionStatus =
      record.detail.cloudProjectionStatus === "SYNCED"
        ? "门店作废与云端记录已同步"
        : record.detail.cloudProjectionStatus === "PENDING"
          ? "门店已完成作废，正在同步到云端"
          : "门店作废已完成，对应云端记录待复核；其他经营可继续";
    return [
      "误开台作废已完成",
      "",
      "",
      record.detail.businessDate,
      "",
      "",
      "",
      "",
      "",
      "",
      cloudProjectionStatus,
      "",
      record.detail.orderNumber,
      record.detail.tableNumber,
      record.detail.reason,
      "未结台单",
      "误开已作废",
      record.detail.previousAmountFen,
      record.detail.currentAmountFen,
      record.detail.paidAmountFen,
      record.detail.refundedAmountFen,
      record.detail.voidedAt,
      ...Array.from({ length: 7 }, () => ""),
    ];
  }
  const cloudProjectionStatus =
    record.detail.cloudProjectionStatus === "SYNCED"
      ? "门店调时与云端记录已同步"
      : record.detail.cloudProjectionStatus === "PENDING"
        ? "门店已完成调时，正在同步到云端"
        : "门店调时已完成，对应云端记录待复核；其他经营可继续";
  return [
    "开台时间更正已完成",
    "",
    "",
    record.detail.businessDate,
    "",
    "",
    "",
    "",
    "",
    "",
    cloudProjectionStatus,
    "",
    record.detail.orderNumber,
    record.detail.tableNumber,
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    "",
    record.detail.reason,
    record.detail.previousStartedAt,
    record.detail.currentStartedAt,
    record.detail.previousTableFeeFen,
    record.detail.currentTableFeeFen,
    record.detail.adjustmentFen,
    record.detail.adjustedAt,
  ];
}

function csvText(value: string): string {
  const neutralized = /^[=+\-@\t\r]/u.test(value) ? `'${value}` : value;
  return `"${neutralized.replaceAll('"', '""')}"`;
}

export function buildBusinessAuditTimelineCsv(timeline: BusinessAuditTimelineReadModel): string {
  if (!isBusinessAuditTimelineReadModel(timeline)) {
    throw new TypeError("门店经营操作记录无法安全生成");
  }
  const rows = timeline.records.map((record) =>
    [
      record.recordNumber,
      record.occurredAt,
      record.actorDisplayName,
      record.actionLabel,
      record.objectLabel,
      record.authorizerDisplayName ?? "无需现场授权",
      ...csvDetailFields(record),
    ]
      .map((value) => csvText(String(value)))
      .join(","),
  );
  return `\uFEFF${BUSINESS_AUDIT_CSV_HEADERS.map(csvText).join(",")}\r\n${rows.join("\r\n")}${rows.length > 0 ? "\r\n" : ""}`;
}

export function businessAuditTimelineFileName(generatedAt: string): string {
  if (!isCanonicalTimestamp(generatedAt)) {
    throw new RangeError("generatedAt must be a canonical UTC timestamp");
  }
  const compact = generatedAt.replace(/[-:]/gu, "");
  return `门店经营操作记录-${compact.slice(0, 8)}-${compact.slice(9, 13)}.csv`;
}
